/**
 * Classifies the transient-network-abort error class that browsers raise when
 * an in-flight `fetch` never completes — most commonly iOS Safari killing the
 * request when the tab is backgrounded mid-call. Safari surfaces this as a
 * `TypeError: Load failed` (no stack frames); other browsers say "Failed to
 * fetch" / "NetworkError" / "Network request failed"; an explicit cancel raises
 * an `AbortError`.
 *
 * None of these mean the photo or the server is bad — the request simply never
 * landed — so callers should treat them as recoverable (offer a retry) and NOT
 * report them to Error Tracking as real bugs.
 *
 * Returns false for plain errors, handled input problems (e.g.
 * `ImageCompressionError`), and HTTP-status failures like `Upload failed (500)`.
 */
export function isTransientNetworkError(error: unknown): boolean {
  // Explicit cancel / abort, regardless of error subclass.
  if (
    error instanceof Error &&
    (error as { name?: string }).name === 'AbortError'
  ) {
    return true
  }

  if (!(error instanceof Error) || !error.message) {
    return false
  }

  const lowerMsg = error.message.toLowerCase()
  return (
    error instanceof TypeError &&
    (lowerMsg.includes('load failed') ||
      lowerMsg.includes('failed to fetch') ||
      lowerMsg.includes('networkerror') ||
      lowerMsg.includes('network request failed'))
  )
}
